import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type DragEvent, type FormEvent } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { api, ApiError, fieldIssues } from '../api';
import { can, fieldVisible, type Me } from '../auth/me';
import { useMe } from '../auth/useMe';
import { formatPln, parsePln, plnToInput } from '../catalog/money';
import { HistoryPanel } from '../history/HistoryPanel';
import { ConflictNotice } from '../ui/ConflictNotice';
import { trapTab } from '../ui/focus';
import { RowMenu } from '../ui/RowMenu';
import { FunctionTasksEditor, tasksToWire, toDraftTask, type DraftTask } from './FunctionTasksEditor';
import { FUNCTION_SETTINGS_KEY, FUNCTIONS_RESOURCE, PEOPLE_KEY, PERSONS_RESOURCE, type FunctionDetail, type FunctionSettings, type FunctionSummary } from './types';
import { dropOn } from '../quotes/lineOrder';
import { refreshCrew } from '../crew/refreshCrew';
import { NumberInput } from '../ui/NumberInput';
import { navKey } from '../nav/session';
import { useUrlState } from '../nav/urlState';
import { DialogLayer } from '../ui/Dialog';
import { leaveGuarded, sameValues, useUnsavedGuard } from '../ui/unsaved';
import { Notice } from '../ui/Notice';

const tabKey = (path: string) => navKey(`people.tab:${path}`);

/**
 * Owner 2026-10-22: going from a person to "Funkcje" and back must land on the same person (and the same open
 * function), unless it was closed. Each tab keeps its last address (search part) in the tab's session storage.
 */
export function rememberPeopleTab(path: string, search: string) {
  try {
    if (search) sessionStorage.setItem(tabKey(path), search);
    else sessionStorage.removeItem(tabKey(path));
  } catch {
    // storage blocked: the tab simply opens fresh
  }
}

/** The address of a tab with what was open there last (or the bare tab). */
export function rememberedPeopleTab(path: string): string {
  try {
    const search = sessionStorage.getItem(tabKey(path));
    return search?.startsWith('?') ? `${path}${search}` : path;
  } catch {
    return path;
  }
}

/** Keeps the current tab's address for the way back. */
export function useRememberPeopleTab() {
  const { pathname, search } = useLocation();
  useEffect(() => rememberPeopleTab(pathname, search), [pathname, search]);
}

/** Set by the person card on its links to a function: where "Wróć" leads. */
export interface FromPerson {
  fromPerson: { to: string; name: string };
}

/** "Osoby" | "Funkcje": the two lists of the section, as far as the role may see them. */
export function PeopleNav({ me }: { me: Me }) {
  const links = [
    ...(can(me, PERSONS_RESOURCE, 'view') ? [{ to: '/osoby', label: 'Osoby' }] : []),
    ...(can(me, FUNCTIONS_RESOURCE, 'view') ? [{ to: '/osoby/funkcje', label: 'Funkcje' }] : []),
  ];
  return (
    <nav className="actions" aria-label="Osoby i funkcje">
      {links.map((l) => (
        <NavLink key={l.to} to={rememberedPeopleTab(l.to)} end className={({ isActive }) => `btn small${isActive ? ' primary' : ''}`}>
          {l.label}
        </NavLink>
      ))}
    </nav>
  );
}

/** Confirmation before a person or a function is deleted (409 shown in place). */
export function ConfirmDelete({ title, text, confirm, url, onClose }: { title: string; text: string; confirm: string; url: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: () => api(url, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: PEOPLE_KEY });
      onClose();
    },
  });
  const error = remove.error instanceof ApiError ? remove.error : null;
  return (
    <div className="dialog-backdrop">
      <div
        className="dialog panel"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="delete-title"
        onKeyDown={(e) => {
          if (e.key === 'Tab') trapTab(e.currentTarget, e.nativeEvent);
        }}
      >
          <DialogLayer onClose={onClose} busy={remove.isPending} />
        <h2 id="delete-title">{title}</h2>
        <p>{text}</p>
        {error?.status === 409 && (
          <ConflictNotice
            onReload={() => {
              void queryClient.invalidateQueries({ queryKey: PEOPLE_KEY });
              onClose();
            }}
          />
        )}
        {error && error.status !== 409 && (
          <p className="alert" role="alert">
            {error.message}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="button" autoFocus disabled={remove.isPending} onClick={() => remove.mutate()}>
            {remove.isPending ? 'Usuwanie…' : confirm}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}

function FunctionForm({ initial, rateVisible, onClose }: { initial?: FunctionDetail; rateVisible: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { me } = useMe();
  const [base, setBase] = useState(initial);
  const canEdit = !initial || (me !== undefined && can(me, FUNCTIONS_RESOURCE, 'edit'));
  // what the role sees decides what the form shows and sends (rule 7a)
  const showRate = rateVisible;
  const showTasks = me === undefined || fieldVisible(me, FUNCTIONS_RESOURCE, 'tasks');
  const [name, setName] = useState(initial?.name ?? '');
  const [rate, setRate] = useState(initial?.defaultRateGrosze != null ? plnToInput(initial.defaultRateGrosze) : '');
  const [tasks, setTasks] = useState<DraftTask[]>((initial?.tasks ?? []).map(toDraftTask));
  const [localError, setLocalError] = useState('');
  const [saved, setSaved] = useState(false);

  const load = (f: FunctionDetail) => {
    setBase(f);
    setName(f.name ?? '');
    setRate(f.defaultRateGrosze != null ? plnToInput(f.defaultRateGrosze) : '');
    setTasks((f.tasks ?? []).map(toDraftTask));
  };

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      base ? api<FunctionDetail>(`/api/people/functions/${base.id}`, { method: 'PATCH', body: { version: base.version, ...body } }) : api<FunctionDetail>('/api/people/functions', { method: 'POST', body }),
    onSuccess: async (f) => {
      load(f);
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: PEOPLE_KEY });
      // owner 2026-10-24: the function's service in Cennik follows (made, renamed)
      await queryClient.invalidateQueries({ queryKey: ['pricing'] });
    },
  });
  const error = save.error instanceof ApiError ? save.error : null;
  const conflict = error?.status === 409;

  /** What to send, or null with the reason shown. */
  function body(): Record<string, unknown> | null {
    let rateGrosze: number | null = null;
    if (showRate && rate.trim() !== '') {
      try {
        rateGrosze = parsePln(rate);
      } catch (e) {
        setLocalError(`Domyślny koszt: ${(e as Error).message}`);
        return null;
      }
    }
    const wire = showTasks ? tasksToWire(tasks) : [];
    if (wire === null) {
      setLocalError('Każde zadanie musi mieć treść, a termin musi być liczbą dni od −365 do 365 (np. −7, 0, 2).');
      return null;
    }
    setLocalError('');
    if (!base) return { name, ...(showRate && rateGrosze !== null ? { defaultRateGrosze: rateGrosze } : {}), ...(wire.length > 0 ? { tasks: wire } : {}) };
    // only what changed: a field the role may not see is never sent (rule 7a)
    const original = JSON.stringify(tasksToWire((base.tasks ?? []).map(toDraftTask)));
    return {
      ...(name !== base.name ? { name } : {}),
      ...(showRate && rateGrosze !== (base.defaultRateGrosze ?? null) ? { defaultRateGrosze: rateGrosze } : {}),
      ...(showTasks && JSON.stringify(wire) !== original ? { tasks: wire } : {}),
    };
  }
  function submit(event: FormEvent) {
    event.preventDefault();
    setSaved(false);
    const b = body();
    if (b) save.mutate(b);
  }
  // owner 2026-10-23 21:14: "Zamknij", another function or another screen with unsaved changes asks first
  const plainTasks = (ts: DraftTask[]) => ts.map(({ key: _key, ...t }) => t);
  const guardRef = useUnsavedGuard({
    dirty:
      canEdit &&
      !sameValues(
        { name, rate, tasks: plainTasks(tasks) },
        { name: base?.name ?? '', rate: base?.defaultRateGrosze != null ? plnToInput(base.defaultRateGrosze) : '', tasks: plainTasks((base?.tasks ?? []).map(toDraftTask)) },
      ),
    save: async () => {
      const b = body();
      if (!b) return false;
      await save.mutateAsync(b);
      return true;
    },
  });

  return (
    <form ref={guardRef} className="panel form wide" aria-label={base ? `Funkcja: ${base.name ?? ''}` : 'Nowa funkcja'} data-feature-id="people.functions" onSubmit={submit}>
      <h2>{base ? `Funkcja: ${base.name ?? ''}` : 'Nowa funkcja'}</h2>
      {conflict && (
        <ConflictNotice
          onReload={() => {
            const current = error.current as FunctionDetail | undefined;
            void (current ? Promise.resolve(current) : api<FunctionDetail>(`/api/people/functions/${base!.id}`)).then(load);
            save.reset();
          }}
        />
      )}
      {error && !conflict && (
        <p className="alert" role="alert">
          {error.message}
          {fieldIssues(error).map((i) => (
            <span key={i.path + i.message}> {i.message}</span>
          ))}
        </p>
      )}
      {localError && (
        <p className="alert" role="alert">
          {localError}
        </p>
      )}
      {saved && !error && (
        <Notice onClose={() => setSaved(false)}>
          Zapisano.
        </Notice>
      )}
      <div className="field-row">
        <label className="field">
          <span>Nazwa</span>
          <input value={name} onChange={(e) => setName(e.target.value)} required maxLength={100} autoComplete="off" readOnly={!canEdit} placeholder="np. Realizator FOH" />
        </label>
        {showRate && (
          <label className="field">
            <span>Domyślny koszt (ile płacimy, zł)</span>
            <NumberInput kind="money" value={rate} onValueChange={setRate} readOnly={!canEdit} placeholder="np. 600" />
            <span className="hint">Koszt firmy za dzień (ile płacimy osobie), gdy osoba nie ma własnego kosztu w tej funkcji. Cenę dla klienta ustawiasz w Cenniku, w grupie „Obsada”.</span>
          </label>
        )}
      </div>
      {showTasks && <FunctionTasksEditor tasks={tasks} onChange={setTasks} canEdit={canEdit} />}
      <div className="actions">
        {canEdit && (
          <button className="btn primary" type="submit" disabled={save.isPending}>
            {save.isPending ? 'Zapisywanie…' : base ? 'Zapisz zmiany' : 'Dodaj funkcję'}
          </button>
        )}
        <button className="btn" type="button" onClick={(e) => leaveGuarded(onClose, e.currentTarget.closest('form'))}>
          Zamknij
        </button>
      </div>
    </form>
  );
}

/** Add (no `functionId`) or edit a function with its task list. */
function FunctionEditor({ functionId, rateVisible, onClose }: { functionId?: string; rateVisible: boolean; onClose: () => void }) {
  const detail = useQuery({
    queryKey: [...PEOPLE_KEY, 'function', functionId],
    enabled: functionId !== undefined,
    gcTime: 0,
    queryFn: () => api<FunctionDetail>(`/api/people/functions/${functionId}`),
  });
  if (functionId === undefined) return <FunctionForm rateVisible={rateVisible} onClose={onClose} />;
  if (detail.error) {
    return (
      <p className="alert" role="alert">
        {detail.error instanceof ApiError ? detail.error.message : 'Nie udało się wczytać funkcji'}
      </p>
    );
  }
  if (!detail.data) return <p className="muted" role="status">Wczytywanie…</p>;
  return <FunctionForm key={detail.data.id} initial={detail.data} rateVisible={rateVisible} onClose={onClose} />;
}

export function FunctionsPage() {
  const { me } = useMe();
  useRememberPeopleTab();
  const back = (useLocation().state as Partial<FromPerson> | null)?.fromPerson;
  // the open function lives in the address (`?funkcja=<id>`, adding: `?nowa=1`), so the way back finds it open
  const { params, update } = useUrlState();
  // `undefined`: editor closed; `null`: adding; an id: editing that function
  const editing: string | null | undefined = params.get('funkcja') ?? (params.get('nowa') === '1' ? null : undefined);
  const setEditing = (id: string | null | undefined) => update({ funkcja: id ?? null, nowa: id === null ? '1' : null });
  const [deleting, setDeleting] = useState<FunctionSummary | null>(null);
  const [historyId, setHistoryId] = useState<string | null>(null);
  const list = useQuery({ queryKey: [...PEOPLE_KEY, 'functions'], queryFn: () => api<{ functions: FunctionSummary[] }>('/api/people/functions') });
  // owner 2026-10-26: the order of the functions is THE order of Obsada (and its ⚙ in „ⓘ Info”) — the owner drags it here
  const isOwner = !!me?.isOwner;
  const settings = useQuery({ queryKey: FUNCTION_SETTINGS_KEY, enabled: isOwner, queryFn: () => api<FunctionSettings>('/api/people/function-settings') });
  const queryClient = useQueryClient();
  const [drag, setDrag] = useState<string | null>(null);
  const reorder = useMutation({
    // review M4: the version of the setting as loaded (never a guess) — and then as the server returned it
    mutationFn: (b: { ids: string[]; version: number }) => api<FunctionSettings>('/api/people/functions/order', { method: 'PUT', body: b }),
    onSuccess: (r) => queryClient.setQueryData(FUNCTION_SETTINGS_KEY, r),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: PEOPLE_KEY });
      await refreshCrew(queryClient);
    },
  });
  // review M4: drag only with the setting loaded, and not while a save is pending
  const mayOrder = isOwner && !!settings.data;
  const dragOn = mayOrder && !reorder.isPending;
  if (!me) return null;
  const fns = list.data?.functions ?? [];
  const rateVisible = fieldVisible(me, FUNCTIONS_RESOURCE, 'defaultRateGrosze');
  const seeHistory = can(me, 'history.entries', 'view');

  return (
    <section className="page" data-feature-id="people.functions">
      <div className="page-head">
        <h1>Osoby i funkcje</h1>
        {can(me, FUNCTIONS_RESOURCE, 'create') && editing === undefined && (
          <button className="btn primary" type="button" onClick={() => leaveGuarded(() => setEditing(null))}>
            Dodaj funkcję
          </button>
        )}
      </div>
      <PeopleNav me={me} />
      {back && (
        <p>
          <Link to={back.to}>‹ Wróć do osoby: {back.name}</Link>
        </p>
      )}
      <p className="muted">
        Funkcje to role przy wydarzeniach, np. „Realizator FOH”, „Technik”, „Kierowca”. Każda ma swoją listę zadań i jest też usługą w{' '}
        {can(me, 'pricing.services', 'view') ? <Link to="/cenniki/uslugi">Cenniku</Link> : 'Cenniku'} (grupa „Obsada”): dodanie funkcji dodaje usługę, zmiana nazwy zmienia obie, cenę ustawiasz w Cenniku.
      </p>

      {editing !== undefined && <FunctionEditor key={editing ?? 'new'} {...(editing !== null ? { functionId: editing } : {})} rateVisible={rateVisible} onClose={() => setEditing(undefined)} />}

      {list.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {list.error && <p className="alert" role="alert">{list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać funkcji'}</p>}
      {list.data && fns.length === 0 && <p className="muted">Nie ma jeszcze żadnej funkcji.</p>}
      {reorder.error instanceof ApiError && reorder.error.status === 409 && <ConflictNotice onReload={() => void queryClient.invalidateQueries({ queryKey: PEOPLE_KEY })} />}
      {reorder.error && !(reorder.error instanceof ApiError && reorder.error.status === 409) && (
        <p className="alert" role="alert">
          {reorder.error instanceof ApiError ? reorder.error.message : 'Nie udało się zmienić kolejności'}
        </p>
      )}
      {fns.length > 0 && (
        <div className="table-wrap">
          <table className="table tpl-table people-table" aria-label="Funkcje">
            <thead>
              <tr>
                <th scope="col">Nazwa</th>
                {rateVisible && (
                  <th scope="col" className="num wide-only">
                    Domyślny koszt
                  </th>
                )}
                <th scope="col" className="num">
                  Zadań
                </th>
                <th scope="col" className="tpl-actions-col">
                  <span className="visually-hidden">Działania</span>
                </th>
              </tr>
            </thead>
            {fns.map((f) => {
              const more = [
                ...(seeHistory ? [{ label: historyId === f.id ? 'Ukryj historię zmian' : 'Historia zmian', onSelect: () => setHistoryId(historyId === f.id ? null : f.id) }] : []),
                ...(can(me, FUNCTIONS_RESOURCE, 'delete') ? [{ label: 'Usuń', danger: true, onSelect: () => setDeleting(f) }] : []),
              ];
              return (
                <tbody
                  key={f.id}
                  aria-label={f.name}
                  className={drag === f.id ? 'dragging' : undefined}
                  {...(mayOrder
                    ? {
                        onDragOver: (e: DragEvent<HTMLTableSectionElement>) => {
                          if (!drag) return;
                          e.preventDefault();
                          e.dataTransfer.dropEffect = 'move';
                        },
                        onDrop: (e: DragEvent<HTMLTableSectionElement>) => {
                          if (!drag) return;
                          e.preventDefault();
                          const box = e.currentTarget.getBoundingClientRect();
                          const next = dropOn(fns.map((x) => x.id), drag, f.id, e.clientY > box.top + box.height / 2);
                          setDrag(null);
                          if (next && settings.data) reorder.mutate({ ids: next, version: settings.data.version });
                        },
                      }
                    : {})}
                >
                  <tr>
                    <td>
                      {mayOrder && (
                        <span
                          className="fn-grip"
                          draggable={dragOn}
                          aria-hidden="true"
                          title="Przeciągnij, żeby zmienić kolejność (ta sama kolejność jest w Obsadzie)"
                          onDragStart={(e) => {
                            if (!dragOn) return e.preventDefault();
                            e.dataTransfer.effectAllowed = 'move';
                            e.dataTransfer.setData('text/plain', f.id);
                            setDrag(f.id);
                          }}
                          onDragEnd={() => setDrag(null)}
                        />
                      )}
                      <strong>{f.name}</strong>
                      {rateVisible && f.defaultRateGrosze != null && <div className="muted phone-only">Domyślny koszt: {formatPln(f.defaultRateGrosze)}</div>}
                    </td>
                    {rateVisible && <td className="num wide-only nowrap">{f.defaultRateGrosze != null ? formatPln(f.defaultRateGrosze) : '—'}</td>}
                    <td className="num">{f.taskCount}</td>
                    <td className="tpl-actions">
                      <button className="btn small" type="button" aria-label={`Edytuj: ${f.name ?? ''}`} onClick={() => leaveGuarded(() => setEditing(f.id))}>
                        {can(me, FUNCTIONS_RESOURCE, 'edit') ? 'Edytuj' : 'Zadania'}
                      </button>
                      <RowMenu label={`Więcej działań: ${f.name ?? ''}`} actions={more} />
                    </td>
                  </tr>
                  {historyId === f.id && seeHistory && (
                    <tr>
                      <td colSpan={rateVisible ? 4 : 3}>
                        <HistoryPanel entityId={f.id} resource={FUNCTIONS_RESOURCE} />
                      </td>
                    </tr>
                  )}
                </tbody>
              );
            })}
          </table>
        </div>
      )}
      {deleting && (
        <ConfirmDelete
          title="Usunąć funkcję?"
          text={`„${deleting.name ?? ''}” zniknie ze słownika i zostanie zdjęta ze wszystkich osób. W historii zmian zostaje wpis o usunięciu.`}
          confirm="Usuń funkcję"
          url={`/api/people/functions/${deleting.id}?version=${deleting.version}`}
          onClose={() => setDeleting(null)}
        />
      )}
    </section>
  );
}
